import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Star, Upload, FileText } from "lucide-react";
import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/shared/components/ui/tooltip";
import { getDocumentTypeLabel } from "@/shared/utils/documentTypeUtils";
import UploadDialog from "../components/UploadDialog";
import { useQueryClient } from "@tanstack/react-query";
import { Database } from "@/shared/types/database.types";

type PersonDocumentRow = Database["public"]["Tables"]["estate_documents"]["Row"];

interface UploadedByUser {
  user_id: number;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

// Extended type for documents with computed display name and joined uploader
type LawyerDocument = PersonDocumentRow & {
  name?: string;
  uploaded_by?: UploadedByUser[] | UploadedByUser | null;
};

const getUploadedByName = (doc: LawyerDocument): string => {
  const joined = doc.uploaded_by;
  const user = Array.isArray(joined) ? joined[0] : joined;
  if (!user) return "—";
  const fullName =
    user.full_name?.trim() || `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return fullName || user.email || "—";
};

interface LawyerDocumentsTabProps {
  documents: LawyerDocument[];
  sortedDocuments: LawyerDocument[];
  isDocFavorited: (docId: number) => boolean;
  toggleFavorite: (docId: number) => void;
  onDocumentClick: (doc: LawyerDocument) => void;
  estateId?: number;
  initialUploadOpen?: boolean;
}

const formatDate = (dateString: string) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function LawyerDocumentsTab({
  documents,
  sortedDocuments,
  isDocFavorited,
  toggleFavorite,
  onDocumentClick,
  estateId,
  initialUploadOpen = false,
}: LawyerDocumentsTabProps) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(initialUploadOpen);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const didOpen = useRef(false);

  useEffect(() => {
    if (initialUploadOpen && !didOpen.current) {
      didOpen.current = true;
      setUploadDialogOpen(true);
      navigate({ pathname, search }, { replace: true, state: null });
    }
  }, [initialUploadOpen, navigate, pathname, search]);

  const handleUploadComplete = () => {
    queryClient.invalidateQueries({ queryKey: ["lawyer-person-documents", String(estateId)] });
    setUploadDialogOpen(false);
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-primary">Uploaded</h3>
            <Button variant="outline" onClick={() => setUploadDialogOpen(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Document
            </Button>
          </div>
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              {!documents || documents.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <FileText className="h-12 w-12 text-tertiary mx-auto mb-4" />
                  <p className="text-secondary">No documents uploaded yet.</p>
                </div>
              ) : (
                <div className="overflow-x-scroll">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-stone-200">
                        <th className="text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                          Document Name
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                          Document Type
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                          Uploaded By
                        </th>
                        <th className="text-left py-3 px-6 text-sm font-medium text-secondary uppercase tracking-wider">
                          Date Uploaded
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDocuments.map((doc) => (
                        <tr
                          key={doc.id}
                          onClick={() => onDocumentClick(doc)}
                          className="border-b border-stone-100 last:border-b-0 hover:bg-stone-50 cursor-pointer transition-colors"
                        >
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-primary">
                                {doc.display_name || doc.name || doc.file_name || "Document"}
                              </span>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFavorite(doc.id);
                                    }}
                                    className="group p-1 rounded-sm hover:bg-stone-200"
                                  >
                                    <Star
                                      className={`h-4 w-4 transition-colors ${
                                        isDocFavorited(doc.id)
                                          ? "fill-yellow-500 stroke-yellow-500"
                                          : "stroke-stone-400 group-hover:fill-yellow-500 group-hover:stroke-yellow-500"
                                      }`}
                                    />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{isDocFavorited(doc.id) ? "Unfavorite" : "Favorite"}</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            {doc.document_type ? (
                              <Badge className="bg-stone-100 text-secondary border-transparent hover:bg-stone-100 font-normal">
                                {getDocumentTypeLabel(doc.document_type)}
                              </Badge>
                            ) : (
                              <span className="text-tertiary">—</span>
                            )}
                          </td>
                          <td className="py-4 px-6">
                            <span className="text-secondary">{getUploadedByName(doc)}</span>
                          </td>
                          <td className="py-4 px-6">
                            <span className="text-secondary">{formatDate(doc.created_at)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Upload Dialog */}
      {estateId && (
        <UploadDialog
          isOpen={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onComplete={handleUploadComplete}
          estateId={estateId}
        />
      )}
    </TooltipProvider>
  );
}
